/* eslint-disable max-len */
import { Container } from 'inversify';
import { Host } from '../../Host';
import { ApiClient } from '../../api/ApiClient';
import { CommandRegistry } from '../../concepts/commands/CommandRegistry';
import { ClientManager, Communicator, ObjectManager } from '../../concepts/objects';
import { RecordSetManager } from '../../concepts/objects/RecordSetManager';
import { TopicManager } from '../../concepts/objects/TopicManager';
import { DefaultPackageHost, PackageType, Resolver } from '../Container';
import { Packages } from '../Packages';
import { PassportModel } from '../PassportModel';
import { createScope } from '../Scope';
import { CorePackage } from './CorePackage';
import { DebugOverlay } from './DebugOverlay';
import { EditorOptions } from './EditorOptions';
import { EditorParams } from './EditorParams';
import { EditorPodObjectManagerBackend } from './EditorPodObjectManagerBackend';
import { EditorPodSession } from './EditorPodSession';
import { localInitialize } from './LocalInitialize';
import { MetadataProvider } from './MetadataProvider';
import {
    cameraFrameContent,
    cameraFrameName,
    cameraFrameSelection,
    cameraLinkToPosition, cameraMoveBy,
    cameraMoveTo, cameraMoveToAll, cameraMoveToFrame, cameraMoveToOrigin, cameraMoveToStart, cameraZoomBy,
    cameraZoomTo
} from './commands/CameraCommands';
import {
    createButton, createCameraButton
} from './commands/ComponentCommands';
import {
    drill
} from './commands/InteractionCommands';
import {
    beginMutating,
    popup,
    rotateReset,
    rotateTo,
    set,
    setName
} from './commands/ManipulationCommands';
import { HistoryPackage } from './history/HistoryPackage';
import { toolEquip, toolReset, toolSetParam } from './tooling/ToolCommands';
import { ToolingPackage } from './tooling/ToolingPackage';

const installServices = (ec: Container, params: EditorParams) => {
    const { boardData, connection, podSession } = params;

    const dataProvider = new MetadataProvider(ec.get(ApiClient), boardData);
    const backend = new EditorPodObjectManagerBackend(boardData.id, podSession, () => true);

    const objects = new ObjectManager(Resolver.createFor(ec), backend);
    const clients = new ClientManager(podSession);
    const comms = new Communicator(podSession, clients);
    const topics = new TopicManager(podSession);
    const recordSets = new RecordSetManager(podSession);

    ec.bind(EditorPodSession).toConstantValue(podSession);
    ec.bind(ClientManager).toConstantValue(clients);
    ec.bind(Communicator).toConstantValue(comms);
    ec.bind(TopicManager).toConstantValue(topics);
    ec.bind(MetadataProvider).toConstantValue(dataProvider);
    ec.bind(ObjectManager).toConstantValue(objects);
    ec.bind(RecordSetManager).toConstantValue(recordSets);

    // If not connected, force remove any write permission to put the editor into readonly mode
    const permissionsData = !!connection && !Host.offline
        ? params.permissionsData
        : params.permissionsData.filter(x => !x.includes('bo:write'));

    ec.bind(PassportModel).toConstantValue(PassportModel.create(permissionsData));
    ec.bind(EditorOptions).toConstantValue(Object.freeze(params.options));

    return Promise.resolve([objects, clients, comms]);
};

const installPackages = async (ec: Container) => {

    const host = new DefaultPackageHost(ec);

    const basePackages: PackageType[] = [
        CorePackage,
        HistoryPackage,
        ToolingPackage,
    ];

    const allPackages = basePackages.concat(Packages);
    allPackages.forEach(t => (new t()).install(host));

    await host.go();

    return [host];
};

const loadDefaultCommands = async (ec: Container) => {
    ec.get(CommandRegistry)
        // Camera
        .add(cameraMoveBy)
        .add(cameraMoveTo)
        .add(cameraMoveToFrame)
        .add(cameraMoveToOrigin)
        .add(cameraZoomBy)
        .add(cameraZoomTo)
        .add(cameraFrameContent)
        .add(cameraFrameSelection)
        .add(cameraLinkToPosition)
        .add(cameraFrameName)
        .add(cameraMoveToStart)
        .add(cameraMoveToAll)
        // Components
        .add(createButton)
        .add(createCameraButton)
        .add(drill)
        // Manipulation
        .add(rotateTo)
        .add(rotateReset)
        // Dev
        // .add(ah)
        // .add(devLogModelState)
        // .add(devRotate)
        // Interaction
        .add(toolSetParam)
        .add(toolEquip)
        .add(toolReset)
        .add(setName)
        .add(set)
        .add(beginMutating)
        .add(popup)
        ;

    return Promise.resolve([]);
};

const loadInitialState = async (ec: Container) => {

    const d0 = ec.resolve(DebugOverlay);
    d0.init();

    localInitialize(ec);
    ec.get(ObjectManager).flushAll();

    return Promise.resolve([d0]);
};

export const EditorScope = createScope<{ params: EditorParams }>({
    async refresh({ params }, c) {
        return [
            ...await installServices(c, params),
            ...await installPackages(c),
            ...await loadDefaultCommands(c),
            ...await loadInitialState(c),
        ];
    }
});
